import type { ActionStep } from './action-types';

/**
 * Media-focused workspaces — the policy catalog from ADR-004.
 *
 * A workspace is a **filter over the interface, never over the document**.
 * Switching to Vector hides raster-only tools and commands; it does not change
 * a single byte of the open file, and every raster layer keeps rendering. That
 * distinction is the whole design: the moment a mode could alter content it
 * stops being a view preference and becomes a destructive setting.
 *
 * Everything that hides or shows a capability reads this one catalog, so a tool
 * cannot disappear from the rail while its menu command stays visible.
 */

/** The workspace the user has chosen. */
export type MediaMode = 'all' | 'raster' | 'vector';

/** What kind of content a capability consumes. */
export type MediaAccepts = 'neutral' | 'raster' | 'vector' | 'text' | 'structure';

/** What running it does to the medium of the thing it touched. */
export type MediaEffect =
  | 'preserve'
  | 'create-raster'
  | 'create-vector'
  | 'rasterize'
  | 'vectorize'
  | 'render-only'
  | 'roundtrip-vector';

export interface MediaPolicy {
  readonly accepts: MediaAccepts;
  readonly effect: MediaEffect;
  /**
   * Present only when running this changes the medium of what it touched. The
   * interface must show this sentence *before* the user commits, never after.
   */
  readonly conversion?: string;
}

export const NEUTRAL: MediaPolicy = { accepts: 'neutral', effect: 'preserve' };
export const RASTER: MediaPolicy = { accepts: 'raster', effect: 'preserve' };
export const VECTOR: MediaPolicy = { accepts: 'vector', effect: 'preserve' };
export const TEXT: MediaPolicy = { accepts: 'text', effect: 'preserve' };

/** Creates pixels where there were none — hidden in a vector workspace. */
export const CREATES_RASTER: MediaPolicy = { accepts: 'neutral', effect: 'create-raster' };

/** Turns paths into pixels. Always announced. */
export const RASTERIZES: MediaPolicy = {
  accepts: 'vector',
  effect: 'rasterize',
  conversion: 'Replaces the selected vector artwork with pixels. Paths, text and stroke data are not recoverable by undoing a later edit.',
};

/** Turns pixels into paths. Always announced. */
export const VECTORIZES: MediaPolicy = {
  accepts: 'raster',
  effect: 'vectorize',
  conversion: 'Replaces raster pixels with paths. Flat areas survive; soft gradients become bands and photographic detail is lost.',
};

/** Rasterize, edit, then optionally trace back. The full round trip. */
export const ROUNDTRIP_VECTOR: MediaPolicy = {
  accepts: 'vector',
  effect: 'roundtrip-vector',
  conversion: 'Rasterizes the selected vector region, edits the pixels, then optionally traces the result back to paths. Flat fills survive; text, strokes, gradients and anchor points do not.',
};

/**
 * Every command exposed by Workspace declares its policy here. Keeping the ids
 * in this typed object means adding a palette action without classifying it is
 * a type error rather than a new way around the workspace filter.
 */
export const EDITOR_ACTION_MEDIA = {
  new: NEUTRAL,
  open: NEUTRAL,
  'place-image': CREATES_RASTER,
  'edit-portable-text': TEXT,
  'new-portable-text': { accepts: 'text', effect: 'create-vector' },
  'install-fonts': NEUTRAL,
  save: NEUTRAL,
  'save-as': NEUTRAL,
  'public-json': NEUTRAL,
  'public-package': NEUTRAL,
  png: RASTER,
  jpeg: RASTER,
  undo: NEUTRAL,
  redo: NEUTRAL,
  duplicate: NEUTRAL,
  'copy-layer': NEUTRAL,
  'paste-layer': NEUTRAL,
  'convert-smart-object': NEUTRAL,
  'edit-smart-object': NEUTRAL,
  'rasterize-smart-object': RASTERIZES,
  'record-action': NEUTRAL,
  'fill-foreground': RASTER,
  'content-aware-fill': RASTER,
  liquify: RASTER,
  'puppet-warp': RASTER,
  'warp-perspective': RASTER,
  'apply-image': RASTER,
  'color-range': RASTER,
  'vanishing-point': NEUTRAL,
  'batch-process': RASTER,
  'data-driven': NEUTRAL,
  'tool-presets': NEUTRAL,
  'high-pass': RASTER,
  displace: RASTER,
  'match-color': RASTER,
  'luminosity-masks': RASTER,
  'content-aware-scale': RASTER,
  'black-white-adjustment': CREATES_RASTER,
  'resize-image': NEUTRAL,
  'invert-layer-colors': RASTER,
  'resize-canvas': NEUTRAL,
  'crop-selection': NEUTRAL,
  'rotate-cw': NEUTRAL,
  'rotate-ccw': NEUTRAL,
  'flip-document-horizontal': NEUTRAL,
  'flip-document-vertical': NEUTRAL,
  'add-guide': NEUTRAL,
  'new-guide-layout': NEUTRAL,
  'clear-guides': NEUTRAL,
  'toggle-grid': NEUTRAL,
  'toggle-snap': NEUTRAL,
  shortcuts: NEUTRAL,
  'select-all': RASTER,
  'clear-selection': RASTER,
  'invert-selection': RASTER,
  'select-same-fill': VECTOR,
  'grow-object-selection': VECTOR,
  'select-touching-regions': VECTOR,
  'group-objects': VECTOR,
  'ungroup-objects': VECTOR,
  'select-all-objects': VECTOR,
  'colour-trace': VECTORIZES,
  'image-trace': VECTORIZES,
  'refine-selection': RASTER,
  'inverse-objects': VECTOR,
  'rotate-180': NEUTRAL,
  recover: NEUTRAL,
} as const satisfies Record<string, MediaPolicy>;

export type EditorActionId = keyof typeof EDITOR_ACTION_MEDIA;

/** Built-in dock tabs use the same policy catalog as commands and tools. */
export const EDITOR_PANEL_MEDIA = {
  layers: NEUTRAL,
  fill: NEUTRAL,
  history: NEUTRAL,
  actions: NEUTRAL,
  channels: RASTER,
  export: RASTER,
  notes: NEUTRAL,
  document: NEUTRAL,
  histogram: NEUTRAL,
  agent: NEUTRAL,
} as const satisfies Record<string, MediaPolicy>;

export type EditorPanelId = keyof typeof EDITOR_PANEL_MEDIA;

/** Recorded actions are preflighted with the same rules before any step runs. */
export function actionStepMedia(step: ActionStep): MediaPolicy {
  switch (step.type) {
    case 'fill-active-raster':
    case 'content-aware-fill':
    case 'clear-selected-pixels':
      return RASTER;
    case 'add-adjustment':
    case 'update-active-adjustment':
      return CREATES_RASTER;
    case 'transform-active-layer':
    case 'duplicate-active-layer':
    case 'marker':
      return NEUTRAL;
  }
}

/**
 * Whether a capability is offered in a workspace.
 *
 * `structure` and `neutral` are always offered: they describe things like
 * groups, notes and navigation that belong to the document rather than to a
 * medium. `text` counts as vector, because that is where type is edited.
 */
export function visibleIn(policy: MediaPolicy, mode: MediaMode): boolean {
  if (mode === 'all') return true;
  // The accepted input alone is not enough. A neutral command that creates
  // pixels is still a pixel operation and must not leak into Vector.
  if (policy.effect === 'rasterize' || policy.effect === 'vectorize' || policy.effect === 'roundtrip-vector') return false;
  if (mode === 'vector' && (policy.effect === 'create-raster' || policy.effect === 'render-only')) return false;
  if (mode === 'raster' && policy.effect === 'create-vector') return false;
  if (policy.accepts === 'neutral' || policy.accepts === 'structure') return true;
  if (mode === 'vector' && policy.accepts === 'text') return true;
  return policy.accepts === mode;
}

/**
 * Whether a capability should be offered given the user's conversion setting.
 *
 * Conversions are hidden by default in a single-medium workspace: someone who
 * has chosen Vector is saying they do not want to be handed pixel operations.
 * Turning the preference on surfaces them as *named* workflows, never as
 * ordinary commands.
 */
export function offeredIn(policy: MediaPolicy, mode: MediaMode, showConversions: boolean): boolean {
  if (mode === 'all') return true;
  if (policy.conversion !== undefined) {
    if (!showConversions) return false;
    if (policy.effect === 'rasterize') return mode === 'raster';
    if (policy.effect === 'vectorize' || policy.effect === 'roundtrip-vector') return mode === 'vector';
    return false;
  }
  return visibleIn(policy, mode);
}

export function modeLabel(mode: MediaMode): string {
  return mode === 'all' ? 'All' : mode === 'raster' ? 'Raster' : 'Vector';
}

export const MEDIA_MODES: readonly MediaMode[] = ['all', 'raster', 'vector'];

const STORAGE_KEY = 'maipo-workspace-media-v1';

/**
 * The chosen workspace is a property of this browser, not of the document. It
 * is deliberately never written into a .maipo file: opening someone else's work
 * must not silently rearrange your interface.
 */
export function storedMediaMode(): MediaMode {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'raster' || value === 'vector' || value === 'all' ? value : 'all';
  } catch {
    return 'all';
  }
}

export function storeMediaMode(mode: MediaMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A browser refusing storage is not a reason to block the switch.
  }
}

export interface ModeSwitchOutcome {
  /** The tool to be active after the switch. */
  readonly tool: string;
  /** True when the active tool was hidden and had to be replaced. */
  readonly toolChanged: boolean;
  /** True when Quick Mask was active and the new workspace cannot host it. */
  readonly exitsQuickMask: boolean;
  /** What to announce. Always says whether the document changed — it never does. */
  readonly announcement: string;
}

/**
 * Works out the consequences of a mode switch before anything is applied.
 *
 * Kept pure and separate from the component so the rules can be tested without
 * a DOM, and so the announcement can never drift from what actually happened.
 */
export function planModeSwitch(input: {
  readonly next: MediaMode;
  readonly activeTool: string;
  readonly activeToolLabel: string;
  readonly activeToolPolicy: MediaPolicy;
  readonly fallbackTool: string;
  readonly quickMaskActive: boolean;
}): ModeSwitchOutcome {
  const keepsTool = visibleIn(input.activeToolPolicy, input.next);
  // Quick Mask paints a pixel selection, so it cannot survive a vector-only
  // workspace. Leaving it silently would strand the user in a mode whose
  // indicator has vanished.
  const exitsQuickMask = input.quickMaskActive && input.next === 'vector';

  const parts = [`${modeLabel(input.next)} workspace.`];
  if (!keepsTool) parts.push(`${input.activeToolLabel} is hidden here, so the active tool fell back to Move.`);
  if (exitsQuickMask) parts.push('Quick Mask was exited.');
  parts.push('This filters the interface only — no document content changed.');

  return {
    tool: keepsTool ? input.activeTool : input.fallbackTool,
    toolChanged: !keepsTool,
    exitsQuickMask,
    announcement: parts.join(' '),
  };
}
